import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, catchError, tap, throwError } from 'rxjs';
import { DateTime } from 'luxon';
import { StateApiError, checkForBadHttpErrorMessage, checkToAddStateApiError, checkToRemoveStateApiError } from '@trovata/app/core/models/state-error.model';
import { StablecoinWalletsService } from '../../../services/wallets/wallets.service';
import { StablecoinBalance, StablecoinBalanceHistoryPoint, StablecoinWallet } from '../../../models/wallet.model';
import { StablecoinDepositInstruction } from '../../../models/deposit-instruction.model';
import {
	ClearStablecoinWalletsState,
	CreateStablecoinDepositInstructions,
	GetStablecoinBalanceHistory,
	GetStablecoinDepositInstructions,
	GetStablecoinWalletBalances,
	GetStablecoinWallets,
	InitStablecoinWalletsState,
	ResetStablecoinWalletsState,
} from '../../actions/wallets.actions';

export interface StablecoinWalletsStateModel {
	wallets: StablecoinWallet[] | null;
	balancesByWalletId: { [walletId: string]: StablecoinBalance[] };
	balanceHistory: StablecoinBalanceHistoryPoint[] | null;
	depositInstructionsByWalletId: { [walletId: string]: StablecoinDepositInstruction[] };
	apiErrors: StateApiError[];
	isCached: boolean;
	walletsLoading: boolean;
	balanceHistoryLoading: boolean;
	depositInstructionsLoading: boolean;
}

const DEFAULT_STABLECOIN_WALLETS_STATE: StablecoinWalletsStateModel = {
	wallets: null,
	balancesByWalletId: {},
	balanceHistory: null,
	depositInstructionsByWalletId: {},
	apiErrors: [],
	isCached: false,
	walletsLoading: true,
	balanceHistoryLoading: true,
	depositInstructionsLoading: false,
};

@State<StablecoinWalletsStateModel>({
	name: 'stablecoinWallets',
	defaults: { ...DEFAULT_STABLECOIN_WALLETS_STATE },
})
@Injectable()
export class StablecoinWalletsState {
	private readonly walletsService: StablecoinWalletsService = inject(StablecoinWalletsService);

	@Selector()
	static wallets(state: StablecoinWalletsStateModel): StablecoinWallet[] | null {
		return state.wallets;
	}

	@Selector()
	static balancesByWalletId(state: StablecoinWalletsStateModel): { [walletId: string]: StablecoinBalance[] } {
		return state.balancesByWalletId;
	}

	@Selector()
	static balanceHistory(state: StablecoinWalletsStateModel): StablecoinBalanceHistoryPoint[] | null {
		return state.balanceHistory;
	}

	@Selector()
	static depositInstructionsByWalletId(state: StablecoinWalletsStateModel): { [walletId: string]: StablecoinDepositInstruction[] } {
		return state.depositInstructionsByWalletId;
	}

	@Selector()
	static walletsLoading(state: StablecoinWalletsStateModel): boolean {
		return state.walletsLoading;
	}

	@Selector()
	static balanceHistoryLoading(state: StablecoinWalletsStateModel): boolean {
		return state.balanceHistoryLoading;
	}

	@Selector()
	static depositInstructionsLoading(state: StablecoinWalletsStateModel): boolean {
		return state.depositInstructionsLoading;
	}

	@Selector()
	static apiErrors(state: StablecoinWalletsStateModel): StateApiError[] {
		return state.apiErrors;
	}

	@Action(InitStablecoinWalletsState)
	init(): void {
		// Fetches are dispatched lazily from page components (no entitlement gate yet).
	}

	@Action(ClearStablecoinWalletsState)
	clear(context: StateContext<StablecoinWalletsStateModel>): void {
		context.setState({ ...DEFAULT_STABLECOIN_WALLETS_STATE });
	}

	@Action(ResetStablecoinWalletsState)
	reset(context: StateContext<StablecoinWalletsStateModel>): void {
		context.setState({ ...DEFAULT_STABLECOIN_WALLETS_STATE });
	}

	@Action(GetStablecoinWallets)
	getWallets(context: StateContext<StablecoinWalletsStateModel>, action: GetStablecoinWallets): Observable<StablecoinWallet[]> | void {
		const state: StablecoinWalletsStateModel = context.getState();
		if (state.isCached && !action.refresh) {
			return;
		}
		context.patchState({ walletsLoading: true });
		return this.walletsService.getWallets().pipe(
			tap((wallets: StablecoinWallet[]) => {
				checkToRemoveStateApiError(context, GetStablecoinWallets);
				context.patchState({ wallets, isCached: true, walletsLoading: false });
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ walletsLoading: false });
				checkToAddStateApiError(error, context, GetStablecoinWallets, checkForBadHttpErrorMessage(error, 'Unable to load wallets.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(GetStablecoinWalletBalances)
	getWalletBalances(context: StateContext<StablecoinWalletsStateModel>, action: GetStablecoinWalletBalances): Observable<StablecoinBalance[]> {
		return this.walletsService.getWalletBalances(action.accountId).pipe(
			tap((balances: StablecoinBalance[]) => {
				checkToRemoveStateApiError(context, GetStablecoinWalletBalances);
				const balancesByWalletId: { [walletId: string]: StablecoinBalance[] } = {
					...context.getState().balancesByWalletId,
					[action.accountId]: balances,
				};
				context.patchState({ balancesByWalletId });
			}),
			catchError((error: HttpErrorResponse) => {
				checkToAddStateApiError(error, context, GetStablecoinWalletBalances, checkForBadHttpErrorMessage(error, 'Unable to load wallet balances.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(GetStablecoinBalanceHistory)
	getBalanceHistory(context: StateContext<StablecoinWalletsStateModel>, action: GetStablecoinBalanceHistory): Observable<StablecoinBalanceHistoryPoint[]> {
		context.patchState({ balanceHistoryLoading: true });
		const toDate: string = DateTime.now().toISODate();
		const fromDate: string = DateTime.now().minus({ days: action.days }).toISODate();
		return this.walletsService.getBalanceHistory(fromDate, toDate, action.accountId).pipe(
			tap((balanceHistory: StablecoinBalanceHistoryPoint[]) => {
				checkToRemoveStateApiError(context, GetStablecoinBalanceHistory);
				context.patchState({ balanceHistory, balanceHistoryLoading: false });
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ balanceHistoryLoading: false });
				checkToAddStateApiError(error, context, GetStablecoinBalanceHistory, checkForBadHttpErrorMessage(error, 'Unable to load balance history.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(GetStablecoinDepositInstructions)
	getDepositInstructions(
		context: StateContext<StablecoinWalletsStateModel>,
		action: GetStablecoinDepositInstructions
	): Observable<StablecoinDepositInstruction[]> {
		context.patchState({ depositInstructionsLoading: true });
		return this.walletsService.getDepositInstructions(action.accountId).pipe(
			tap((instructions: StablecoinDepositInstruction[]) => {
				checkToRemoveStateApiError(context, GetStablecoinDepositInstructions);
				const depositInstructionsByWalletId: { [walletId: string]: StablecoinDepositInstruction[] } = {
					...context.getState().depositInstructionsByWalletId,
					[action.accountId]: instructions,
				};
				context.patchState({ depositInstructionsByWalletId, depositInstructionsLoading: false });
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ depositInstructionsLoading: false });
				checkToAddStateApiError(
					error,
					context,
					GetStablecoinDepositInstructions,
					checkForBadHttpErrorMessage(error, 'Unable to load funding instructions.').message
				);
				return throwError(() => error);
			})
		);
	}

	@Action(CreateStablecoinDepositInstructions)
	createDepositInstructions(
		context: StateContext<StablecoinWalletsStateModel>,
		action: CreateStablecoinDepositInstructions
	): Observable<StablecoinDepositInstruction> {
		context.patchState({ depositInstructionsLoading: true });
		return this.walletsService.createDepositInstructions(action.body).pipe(
			tap((instruction: StablecoinDepositInstruction) => {
				checkToRemoveStateApiError(context, CreateStablecoinDepositInstructions);
				const state: StablecoinWalletsStateModel = context.getState();
				const existing: StablecoinDepositInstruction[] = state.depositInstructionsByWalletId[action.body.account_id] ?? [];
				const withoutDuplicate: StablecoinDepositInstruction[] = existing.filter((item: StablecoinDepositInstruction) => item.id !== instruction.id);
				context.patchState({
					depositInstructionsByWalletId: {
						...state.depositInstructionsByWalletId,
						[action.body.account_id]: [instruction, ...withoutDuplicate],
					},
					depositInstructionsLoading: false,
				});
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ depositInstructionsLoading: false });
				checkToAddStateApiError(
					error,
					context,
					CreateStablecoinDepositInstructions,
					checkForBadHttpErrorMessage(error, 'Unable to create funding instructions.').message
				);
				return throwError(() => error);
			})
		);
	}
}
